import { useState, useEffect, useRef, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────
// Retry / refresh config
// ─────────────────────────────────────────────────────────────
const MAX_RETRIES    = 3;    // total extra attempts after the first
const BASE_DELAY_MS  = 800;  // first retry delay; doubles each attempt
const REFRESH_RATIO  = 0.80; // refresh the token at 80 % of its TTL

/**
 * useViewerToken
 *
 * Fetches a short-lived APS access token from
 * the backend proxy at /api/auth/token.
 *
 * Expected JSON shape from the server:
 *   { access_token: string, expires_in?: number }
 *
 * Behaviour
 * ─────────
 * • loading     true until the first successful fetch or final failure.
 * • isRefreshing true during silent background refreshes (token already
 *                set; viewer stays mounted).
 * • If access_token is missing/null the hook retries up to MAX_RETRIES
 *   times with exponential back-off before settling on error.
 * • After every successful fetch a background refresh is scheduled at
 *   REFRESH_RATIO × expires_in so the token never goes stale mid-session.
 * • The raw token is held only in React state — never written to
 *   localStorage, sessionStorage, or any ref visible outside this hook.
 * • Cleanup: any in-flight retry / refresh timer is cancelled on unmount.
 * • retry() — stable callback; call it from a "Try again" button after
 *   a hard failure to restart the full fetch cycle.
 */
export function useViewerToken() {
    const [token,        setToken]        = useState(null);
    const [expiresIn,    setExpiresIn]    = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Track retry attempt count across re-runs without triggering re-render.
    const attemptRef = useRef(0);
    // Hold the setTimeout id so we can cancel on unmount or re-schedule.
    const timerRef   = useRef(null);
    // Prevent state updates after unmount.
    const mountedRef = useRef(true);

    // ─────────────────────────────────────────────────────────
    // fetchToken
    //   silent — true on background refreshes; keeps the viewer
    //            alive and only flips isRefreshing, not loading.
    // ─────────────────────────────────────────────────────────
    const fetchToken = useCallback(async ({ silent = false } = {}) => {
        if (silent && mountedRef.current) setIsRefreshing(true);

        try {
            const res = await fetch('/api/auth/token');

            if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);

            const data        = await res.json();
            const accessToken = data.access_token ?? null;
            const ttl         = typeof data.expires_in === 'number'
                ? data.expires_in
                : 3600;

            // ── Token missing → retry with back-off ───────────
            if (!accessToken) {
                if (attemptRef.current < MAX_RETRIES) {
                    const delay = BASE_DELAY_MS * 2 ** attemptRef.current;
                    attemptRef.current += 1;
                    console.warn(
                        `[useViewerToken] access_token was empty — ` +
                        `retrying in ${delay}ms ` +
                        `(attempt ${attemptRef.current}/${MAX_RETRIES})`,
                    );
                    timerRef.current = setTimeout(() => {
                        if (mountedRef.current) fetchToken({ silent });
                    }, delay);
                    return; // stay in loading / refreshing state during back-off
                }
                throw new Error('access_token was empty after maximum retries.');
            }

            // ── Success ────────────────────────────────────────
            if (!mountedRef.current) return;
            setToken(accessToken);
            setExpiresIn(ttl);
            setError(null);

            // ── Schedule the next silent refresh ───────────────
            // Fires at REFRESH_RATIO × ttl so there is always a
            // comfortable buffer before APS rejects the token.
            const refreshDelay = Math.floor(ttl * REFRESH_RATIO) * 1000;
            console.log(
                `[useViewerToken] token valid for ${ttl}s — ` +
                `next refresh in ${refreshDelay / 1000}s`,
            );
            timerRef.current = setTimeout(() => {
                if (mountedRef.current) {
                    attemptRef.current = 0; // reset retry counter for the refresh cycle
                    fetchToken({ silent: true });
                }
            }, refreshDelay);

        } catch (err) {
            if (!mountedRef.current) return;
            console.error('[useViewerToken] fetch failed:', err.message);
            setError(err.message);
        } finally {
            if (mountedRef.current) {
                // Set loading / isRefreshing false only when fully resolved —
                // success or final failure.  Mid-retry runs skip this so the
                // loading spinner / refresh banner stays visible during back-off.
                const retriesExhausted = attemptRef.current >= MAX_RETRIES;
                const noRetryQueued    = attemptRef.current === 0;
                if (noRetryQueued || retriesExhausted) {
                    setLoading(false);
                    setIsRefreshing(false);
                }
            }
        }
    }, []); // stable reference — no deps change after mount

    // ─────────────────────────────────────────────────────────
    // retry — exposed so the UI can offer a "Try again" button
    // after a hard failure.  Resets all state and restarts the
    // full fetch cycle from scratch.
    // ─────────────────────────────────────────────────────────
    const retry = useCallback(() => {
        clearTimeout(timerRef.current);
        attemptRef.current = 0;
        setLoading(true);
        setError(null);
        fetchToken();
    }, [fetchToken]);

    useEffect(() => {
        mountedRef.current = true;
        attemptRef.current = 0;

        fetchToken();

        return () => {
            mountedRef.current = false;
            clearTimeout(timerRef.current);
        };
    }, [fetchToken]);

    return { token, expiresIn, loading, error, isRefreshing, retry };
}